import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Package, Loader2 } from 'lucide-react';
import { authApi } from '@/api/authApi';
import { useAuthStore } from '@/store/authStore';
import type { LoginRequest } from '@/types/api';
import { handleApiError } from '@/api/client';
import { ErrorAlert } from '@/components/common/ErrorAlert';

export function Login() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState<string | null>(null);
  
  // Check for auth error messages (from session expiration redirect)
  useEffect(() => {
    const authError = sessionStorage.getItem('auth_error');
    if (authError) {
      setSessionExpired(authError);
      sessionStorage.removeItem('auth_error');
    }
  }, []);
  
  const { register, handleSubmit, formState: { errors } } = useForm<LoginRequest>();
  
  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (data) => {
      login(data);
      navigate('/');
    },
    onError: (err) => {
      setError(handleApiError(err as any));
    },
  });

  const onSubmit = (data: LoginRequest) => {
    setError(null);
    loginMutation.mutate(data);
  };

  return (
    <div className="min-h-screen bg-surface-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-450/10 border border-amber-200 dark:border-amber-450/20 mb-4">
            <Package className="w-8 h-8 text-amber-600 dark:text-amber-450" />
          </div>
          <h1 className="font-sans text-3xl font-bold text-gray-900 dark:text-warm-cream mb-2">
            Fun 1st Treasures
          </h1>
          <p className="text-gray-500 dark:text-warm-sand">Inventory Management System</p>
        </div>

        {/* Login Form */}
        <div className="card p-8">
          <h2 className="font-sans text-xl font-semibold text-gray-900 dark:text-warm-cream mb-6">
            Sign in to your account
          </h2>

          {sessionExpired && (
            <div className="mb-4">
              <ErrorAlert title="Session Expired" message={sessionExpired} severity="info" />
            </div>
          )}

          {error && (
            <div className="mb-4">
              <ErrorAlert title="Login Failed" message={error} />
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-warm-sand mb-2">Email Address</label>
              <input
                type="email"
                className="input"
                placeholder="admin@fun1sttreasures.com"
                {...register('email', { required: 'Email is required' })}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-600 dark:text-red-400">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-warm-sand mb-2">Password</label>
              <input
                type="password"
                className="input"
                placeholder="••••••••"
                {...register('password', { required: 'Password is required' })}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-600 dark:text-red-400">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="btn-primary w-full mt-6"
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>


        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 dark:text-warm-sand/60 mt-8">
          © 2026 Fun 1st Treasures. All rights reserved.
          <span className="block mt-1 text-xs text-gray-400 dark:text-warm-sand/40">v{__APP_VERSION__}</span>
        </p>
      </div>
    </div>
  );
}
